import { useState, useEffect, useCallback } from "react";
import { getAllSurveys, getSurveyCompletionCount } from "api/cansurvey/surveyApi";
import type { SurveySummary, GetAllSurveysResponse, PaginationInfo } from "types/survey";

interface UseSurveysResult {
  surveys: SurveySummary[];
  pagination: PaginationInfo | null;
  responses: { [key: number]: number | null };
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export const useSurveys = (
  page: number = 0,
  pageSize: number = 25
): UseSurveysResult => {
  const [surveys, setSurveys] = useState<SurveySummary[]>([]);
  const [pagination, setPagination] = useState<PaginationInfo | null>(null);
  const [responses, setResponses] = useState<{ [key: number]: number | null }>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchSurveys = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const data: GetAllSurveysResponse = await getAllSurveys(page, pageSize);

      if (!Array.isArray(data.surveys)) {
        throw new Error("Invalid response format from API");
      }

      setSurveys(data.surveys);
      setPagination(data.pagination);

      // Initialize response counts
      const initialResponses: { [key: number]: number | null } = {};
      data.surveys.forEach((survey) => {
        initialResponses[survey.SurveyId] = null;
      });
      setResponses(initialResponses);

      // Fetch completion counts for each survey
      data.surveys.forEach(async (survey) => {
        try {
          const countData = await getSurveyCompletionCount(survey.SurveyId);
          setResponses((prev) => ({
            ...prev,
            [survey.SurveyId]: countData.completionCount,
          }));
        } catch (err) {
          // Silently fail for individual count fetches
          console.error(`Error fetching completion count for survey ${survey.SurveyId}:`, err);
        }
      });
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : "An error occurred while loading surveys. This may be due to insufficient permissions or a server issue.";
      setError(errorMessage);
      setSurveys([]);
      setPagination(null);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize]);

  useEffect(() => {
    fetchSurveys();
  }, [fetchSurveys]);

  return {
    surveys,
    pagination,
    responses,
    loading,
    error,
    refetch: fetchSurveys,
  };
};
